import styled, { css } from 'styled-components';

import { MAP_CONTROLS_LAYOUT } from './constants';

/**
 * Slot for the lateral readout, bottom-aligned with the rail beside it.
 *
 * Exists so the cluster can address the readout on its own — see the panel rule
 * in ClusterContainer.
 */
export const AsideSlot = styled.div`
	display: flex;
	align-items: flex-end;
	pointer-events: none;
`;

/**
 * Outer box of the bottom-right controls cluster.
 *
 * Anchored to the bottom-right corner and laid out as a row: the lateral
 * readout (scale bar) on the left, the vertical control rail on the right.
 * Because it is bottom-anchored with an implicit height, the rail grows
 * upward as controls are added.
 */
export const ClusterContainer = styled.div`
	position: absolute;
	right: ${MAP_CONTROLS_LAYOUT.right}px;
	bottom: ${({ $bottom }) => $bottom}px;
	z-index: ${MAP_CONTROLS_LAYOUT.zIndex};

	display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
	align-items: flex-end;
	gap: ${MAP_CONTROLS_LAYOUT.asideGap}px;

	/*
	 * The cluster's box spans the gaps between its controls. Those gaps must stay
	 * transparent to the map underneath, so the container ignores pointer events
	 * and each interactive control opts back in with pointer-events: auto.
	 */
	pointer-events: none;

	transition: bottom 0.2s ease-in-out;

	/*
	 * Rail panels open leftward, across the readout's slot — opening upward would
	 * cover the controls stacked above them. Drop the readout while a panel is
	 * open rather than letting the two overlap; it is passive, the panel is not.
	 */
	&:has([data-rail-panel]) ${AsideSlot} {
		visibility: hidden;
	}
`;

/** Vertical stack of controls, bottom-anchored and growing upward. */
export const ControlRail = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: ${MAP_CONTROLS_LAYOUT.railGap}px;
	pointer-events: none;

	/*
	 * Pin the rail's layout width to the button size so that wider children
	 * (e.g. the CHM color legend) overflow to the left without pushing the
	 * aside slot (scale bar) sideways.
	 */
	width: ${MAP_CONTROLS_LAYOUT.buttonSize}px;
`;

/**
 * Shared chrome for a square icon button in the control rail.
 *
 * Tier 3 (custom): antd has no translucent, blurred surface that floats over a map; its
 * hover surface is the custom theme key `panelGlassBg`. Catalogue:
 * .claude/skills/design-to-component/catalogue.md ("Side panel (glass)").
 *
 * Controls supply only an icon and behaviour; size, surface and states live
 * here so every button in the rail stays visually identical. Pass `$active`
 * for buttons that toggle a mode (and mirror it with `aria-pressed`).
 */
export const MapControlButton = styled.button`
	position: relative;
	pointer-events: auto;

	width: ${MAP_CONTROLS_LAYOUT.buttonSize}px;
	height: ${MAP_CONTROLS_LAYOUT.buttonSize}px;
	border-radius: 8px;
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	background: ${({ theme }) => theme.colors.background};
	color: ${({ theme }) => theme.colors.textColor};
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	cursor: pointer;

	display: flex;
	align-items: center;
	justify-content: center;

	/* react-icons size themselves in em units, so the button owns the glyph size. */
	font-size: ${MAP_CONTROLS_LAYOUT.iconSize}px;

	transition: all 0.15s ease;
	backdrop-filter: blur(12px);

	&:hover:not(:disabled) {
		background: ${({ theme }) => theme.token.panelGlassBg};
		transform: scale(1.05);
	}

	/* Disabled while another flow owns the map; it must read as unavailable
	   rather than silently swallowing the click. */
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.green};
		outline-offset: 2px;
	}

	${({ $active, theme }) =>
		$active &&
		css`
			border-color: ${theme.colors.green};
			background: ${theme.colors.green}20;
			color: ${theme.colors.green};

			&:hover:not(:disabled) {
				background: ${theme.colors.green}30;
			}
		`}
`;
